import { Award } from "lucide-react";
import SceneBackdrop from "@/components/SceneBackdrop";

/**
 * CertificationsStrip — luxury 3-badge trust strip
 * Renders ASME · CE · ISO 9001:2015 as crisp inline SVG badges.
 * Placed on the /hyperbaric page to reinforce clinical-grade credentials.
 */
export default function CertificationsStrip() {
  return (
    <section
      data-testid="certifications-strip"
      className="relative overflow-hidden bg-[#FAF9F6] border-y border-[#E5E2DA] py-20 lg:py-24"
    >
      <SceneBackdrop variant="cream" label="RENEW" art="rings" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 overline text-[#C9A961]">
            <Award size={14} strokeWidth={1.5} />
            Clinical-Grade Certifications
          </div>
          <h2 className="font-serif-editorial text-3xl md:text-4xl lg:text-5xl mt-6 leading-tight text-[#1C1C1C]">
            Engineered to the world's<br />
            <em className="italic text-[#C9A961]">strictest safety standards.</em>
          </h2>
          <p className="mt-6 text-sm md:text-base text-[#5C5C5C] leading-relaxed">
            Every TheraTec hyperbaric chamber is manufactured under three internationally recognised certifications — because pressure vessels holding your clients demand nothing less.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* ASME */}
          <div
            data-testid="cert-asme"
            className="bg-white border border-[#E5E2DA] p-10 flex flex-col items-center text-center transition-all duration-500 hover:border-[#C9A961]/60 hover:-translate-y-1 shadow-sm"
          >
            <AsmeBadge />
            <div className="mt-8 overline text-[#C9A961]">Certified by</div>
            <div className="mt-3 font-serif-editorial text-xl text-[#1C1C1C] leading-tight">
              American Society of Mechanical Engineers
            </div>
            <p className="mt-4 text-xs text-[#5C5C5C] leading-relaxed max-w-xs">
              Pressure vessel design and manufacturing certified to ASME Section VIII standards — the global benchmark for hyperbaric equipment safety.
            </p>
          </div>

          {/* CE */}
          <div
            data-testid="cert-ce"
            className="bg-white border border-[#E5E2DA] p-10 flex flex-col items-center text-center transition-all duration-500 hover:border-[#C9A961]/60 hover:-translate-y-1 shadow-sm"
          >
            <CeBadge />
            <div className="mt-8 overline text-[#C9A961]">CE Marked</div>
            <div className="mt-3 font-serif-editorial text-xl text-[#1C1C1C] leading-tight">
              European Conformity
            </div>
            <p className="mt-4 text-xs text-[#5C5C5C] leading-relaxed max-w-xs">
              Compliant with all applicable European health, safety, and environmental protection directives for pressure equipment (PED 2014/68/EU).
            </p>
          </div>

          {/* ISO 9001 */}
          <div
            data-testid="cert-iso"
            className="bg-white border border-[#E5E2DA] p-10 flex flex-col items-center text-center transition-all duration-500 hover:border-[#C9A961]/60 hover:-translate-y-1 shadow-sm"
          >
            <IsoBadge />
            <div className="mt-8 overline text-[#C9A961]">ISO 9001:2015</div>
            <div className="mt-3 font-serif-editorial text-xl text-[#1C1C1C] leading-tight">
              Certified Quality Management
            </div>
            <p className="mt-4 text-xs text-[#5C5C5C] leading-relaxed max-w-xs">
              Full quality-management system certification — every unit is traceable from raw material through installation, with rigorous documentation at every stage.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Inline SVG badges — luxury tone ---------- */

function AsmeBadge() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" aria-label="ASME certification" role="img">
      <defs>
        <linearGradient id="asme-g" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E8D6A8" />
          <stop offset="100%" stopColor="#C9A961" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="56" fill="none" stroke="url(#asme-g)" strokeWidth="1.5" opacity="0.6" />
      <circle cx="60" cy="60" r="48" fill="none" stroke="url(#asme-g)" strokeWidth="1" opacity="0.4" />
      {/* Globe */}
      <circle cx="60" cy="42" r="12" fill="none" stroke="url(#asme-g)" strokeWidth="1.2" />
      <ellipse cx="60" cy="42" rx="12" ry="5" fill="none" stroke="url(#asme-g)" strokeWidth="0.8" />
      <line x1="60" y1="30" x2="60" y2="54" stroke="url(#asme-g)" strokeWidth="0.8" />
      {/* ASME text */}
      <text
        x="60"
        y="82"
        textAnchor="middle"
        fontFamily="SF Pro Display, sans-serif"
        fontWeight="700"
        fontSize="22"
        letterSpacing="2"
        fill="url(#asme-g)"
      >
        ASME
      </text>
      <text
        x="60"
        y="98"
        textAnchor="middle"
        fontFamily="SF Pro Display, sans-serif"
        fontWeight="500"
        fontSize="7"
        letterSpacing="3"
        fill="#5C5C5C"
        opacity="0.7"
      >
        CERTIFIED
      </text>
    </svg>
  );
}

function CeBadge() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" aria-label="CE certification" role="img">
      <defs>
        <linearGradient id="ce-g" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E8D6A8" />
          <stop offset="100%" stopColor="#C9A961" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="56" fill="none" stroke="url(#ce-g)" strokeWidth="1.5" opacity="0.6" />
      <circle cx="60" cy="60" r="48" fill="none" stroke="url(#ce-g)" strokeWidth="1" opacity="0.4" />
      {/* C */}
      <path
        d="M 46 45 A 18 18 0 1 0 46 75"
        fill="none"
        stroke="url(#ce-g)"
        strokeWidth="4"
        strokeLinecap="round"
      />
      {/* E */}
      <path
        d="M 66 45 A 18 18 0 1 1 66 75 M 66 60 L 78 60"
        fill="none"
        stroke="url(#ce-g)"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <text
        x="60"
        y="100"
        textAnchor="middle"
        fontFamily="SF Pro Display, sans-serif"
        fontWeight="500"
        fontSize="7"
        letterSpacing="3"
        fill="#5C5C5C"
        opacity="0.7"
      >
        MARK
      </text>
    </svg>
  );
}

function IsoBadge() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" aria-label="ISO 9001 certification" role="img">
      <defs>
        <linearGradient id="iso-g" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E8D6A8" />
          <stop offset="100%" stopColor="#C9A961" />
        </linearGradient>
        <path
          id="iso-top-arc"
          d="M 20 60 A 40 40 0 0 1 100 60"
          fill="none"
        />
        <path
          id="iso-bottom-arc"
          d="M 22 62 A 38 38 0 0 0 98 62"
          fill="none"
        />
      </defs>
      <circle cx="60" cy="60" r="56" fill="none" stroke="url(#iso-g)" strokeWidth="1.5" opacity="0.6" />
      <circle cx="60" cy="60" r="48" fill="none" stroke="url(#iso-g)" strokeWidth="1" opacity="0.4" />
      {/* Top arc text */}
      <text fontFamily="SF Pro Display, sans-serif" fontWeight="600" fontSize="7" letterSpacing="4" fill="#5C5C5C" opacity="0.7">
        <textPath href="#iso-top-arc" startOffset="50%" textAnchor="middle">
          CERTIFIED
        </textPath>
      </text>
      {/* ISO text */}
      <text
        x="60"
        y="66"
        textAnchor="middle"
        fontFamily="SF Pro Display, sans-serif"
        fontWeight="700"
        fontSize="24"
        letterSpacing="2"
        fill="url(#iso-g)"
      >
        ISO
      </text>
      <text
        x="60"
        y="80"
        textAnchor="middle"
        fontFamily="SF Pro Display, sans-serif"
        fontWeight="600"
        fontSize="9"
        letterSpacing="1"
        fill="url(#iso-g)"
      >
        9001:2015
      </text>
      <line x1="30" y1="88" x2="90" y2="88" stroke="url(#iso-g)" strokeWidth="0.8" opacity="0.5" />
      <text
        x="60"
        y="102"
        textAnchor="middle"
        fontFamily="SF Pro Display, sans-serif"
        fontWeight="500"
        fontSize="6"
        letterSpacing="3"
        fill="#5C5C5C"
        opacity="0.7"
      >
        QUALITY MANAGEMENT
      </text>
    </svg>
  );
}
